import { useState, useEffect, useCallback } from 'react';
import { C, F, GREEN } from '../constants';

const API_URL = process.env.REACT_APP_MEDIA_STUDIO_API_URL || 'https://strategic-honesty-media-studio-production.up.railway.app';
const API_KEY = process.env.REACT_APP_API_KEY;

// LinkedIn posts never auto-publish — see statusForNewPost in
// media-studio-v2's bufferQueue.cjs. Every LinkedIn post, regardless of
// where it came from (including an external app pushing straight into the
// queue), lands here first and stays here until a human explicitly approves
// it. Approving moves it into the normal Awaiting Publish queue; rejecting
// deletes it outright (nothing was ever "live" to begin with, so there's no
// restore path the way quarantine has one).
export default function NeedsApprovalPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true); // open by default — this needs eyes on it
  const [busyId, setBusyId] = useState(null);
  const [loadError, setLoadError] = useState(null); // { status } when the list itself could not be loaded
  const [errors, setErrors] = useState({}); // itemId -> message, so a failed click is visible instead of silently no-op-ing

  const fetchNeedsApproval = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/buffer-queue/needs-approval`, { headers: { 'x-api-key': API_KEY } });
      if (!res.ok) {
        setLoadError({ status: res.status });
        setItems([]);
        return;
      }
      setLoadError(null);
      const data = await res.json();
      setItems(data.success ? (data.posts || []) : []);
    } catch {
      setLoadError({ status: 'network' });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNeedsApproval(); }, [fetchNeedsApproval]);

  // Both actions previously fired-and-forgot the fetch — a failed request
  // (rate limit, network blip, stale/expired approval, anything) looked
  // identical to success from the button's point of view: no error, item
  // just stayed in the list. That's what made a real failure look like "I
  // clicked Approve and nothing happened" — clicking again was the only
  // way to notice it actually needed a retry. Now every non-ok response is
  // surfaced inline instead of swallowed.
  async function approve(id) {
    setBusyId(id);
    setErrors(prev => ({ ...prev, [id]: null }));
    try {
      const res = await fetch(`${API_URL}/api/buffer-queue/${id}/approve`, { method: 'PATCH', headers: { 'x-api-key': API_KEY } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setErrors(prev => ({ ...prev, [id]: data.error || `Approve failed (HTTP ${res.status}) — try again` }));
        return;
      }
      await fetchNeedsApproval();
    } catch (e) {
      setErrors(prev => ({ ...prev, [id]: 'Network error — approve did not go through, try again' }));
    } finally {
      setBusyId(null);
    }
  }

  async function reject(id) {
    if (!window.confirm('Reject and delete this LinkedIn draft? This cannot be undone.')) return;
    setBusyId(id);
    setErrors(prev => ({ ...prev, [id]: null }));
    try {
      const res = await fetch(`${API_URL}/api/buffer-queue/${id}`, { method: 'DELETE', headers: { 'x-api-key': API_KEY } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setErrors(prev => ({ ...prev, [id]: data.error || `Reject failed (HTTP ${res.status}) — try again` }));
        return;
      }
      await fetchNeedsApproval();
    } catch (e) {
      setErrors(prev => ({ ...prev, [id]: 'Network error — reject did not go through, try again' }));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 16 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>🔎</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>LinkedIn — Needs Your Approval</span>
          <span style={{ fontSize: 11, color: '#fff', background: items.length ? '#0a66c2' : '#94a3b8', borderRadius: 10, padding: '1px 8px', fontWeight: 600 }}>{items.length}</span>
        </div>
        <span style={{ fontSize: 12, color: C.muted }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          {loading ? (
            <div style={{ fontSize: 12, color: C.muted }}>Loading…</div>
          ) : loadError ? (
            <div style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>Couldn't load this list ({loadError.status === 'network' ? 'backend unreachable' : `HTTP ${loadError.status}`}) — posts waiting for approval are not being shown.</div>
          ) : items.length === 0 ? (
            <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>Nothing waiting — new LinkedIn posts (including ones pushed in by other apps) will show up here for your approval before they can go out.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {items.map(item => (
                <div key={item.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, background: '#eff6ff' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#dbeafe', color: '#0a66c2' }}>linkedin</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{item.title || '(untitled)'}</span>
                    <span style={{ fontSize: 10, color: C.muted, marginLeft: 'auto' }}>{item.created_at ? new Date(item.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</span>
                  </div>
                  {item.content && (
                    <div style={{ fontSize: 12, color: C.text, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 6, padding: 8, marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                      {item.content}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => approve(item.id)} disabled={busyId === item.id} style={{ padding: '6px 14px', background: busyId === item.id ? '#e0e0e0' : GREEN, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: busyId === item.id ? 'not-allowed' : 'pointer', fontFamily: F }}>
                      ✓ Approve — queue for publish
                    </button>
                    <button onClick={() => reject(item.id)} disabled={busyId === item.id} style={{ padding: '6px 14px', background: '#fff', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: busyId === item.id ? 'not-allowed' : 'pointer', fontFamily: F }}>
                      ✗ Reject
                    </button>
                  </div>
                  {errors[item.id] && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626' }}>✗ {errors[item.id]}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
